import { useEffect, useState, useCallback, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import VoiceRecorder from "@/components/VoiceRecorder";
import EntryCard from "@/components/EntryCard";
import WeeklyInsights from "@/components/WeeklyInsights";
import StreakTracker from "@/components/StreakTracker";
import NudgeBanner from "@/components/NudgeBanner";
import ReflectionCard from "@/components/ReflectionCard";
import WeeklyReflectionCard from "@/components/WeeklyReflectionCard";
import AIConsentModal from "@/components/AIConsentModal";
import SettingsDrawer from "@/components/SettingsDrawer";
import ReminderEngine from "@/components/ReminderEngine";
import { MagnifyingGlass, SignOut, BookOpenText, GearSix } from "@phosphor-icons/react";

export default function Dashboard() {
  const { user, logout, setUser } = useAuth();
  const navigate = useNavigate();
  const [entries, setEntries] = useState([]);
  const [insights, setInsights] = useState(null);
  const [streak, setStreak] = useState({ current_streak: 0, last_7_days: [] });
  const [insightsLoading, setInsightsLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [search, setSearch] = useState("");
  const [submitError, setSubmitError] = useState("");
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [nudgeDismissed, setNudgeDismissed] = useState(false);
  const [latestReflection, setLatestReflection] = useState("");
  const [latestEntryId, setLatestEntryId] = useState("");
  const [consentOpen, setConsentOpen] = useState(false);
  const [pendingRecording, setPendingRecording] = useState(null); // { blob, duration }

  const hasConsent = Boolean(user?.ai_consent_at) || (typeof window !== "undefined" && Boolean(window.localStorage?.getItem("hush_ai_consent_at")));

  const loadEntries = useCallback(async (q, signal) => {
    try {
      const { data } = await api.get("/journal/entries", {
        params: q ? { q } : {},
        signal,
      });
      // Defensive: only accept arrays. Some WebView/proxy edge cases have
      // been observed returning a non-array body (object, empty string),
      // which would crash the EntryCard list render with `u.map is not a function`.
      setEntries(Array.isArray(data) ? data : []);
    } catch (err) {
      if (err?.name === "CanceledError" || err?.code === "ERR_CANCELED") return;
      // Telemetry: surface why loadEntries fails so we can see if it's a 401,
      // network error, or something else. Native iOS users were seeing
      // successful uploads but an empty dashboard because this swallowed
      // every error including auth failures.
      const status = err?.response?.status;
      const code = err?.code || "";
      const detail = err?.response?.data?.detail || err?.message || "unknown";
      const payload = {
        message: `[loadEntries] fail: status=${status} code=${code} detail=${String(detail).slice(0, 200)}`,
        url: typeof window !== "undefined" ? window.location.href : "",
        ua: typeof navigator !== "undefined" ? navigator.userAgent : "",
        platform: "ios-android-web",
        app_version: "1.1.0",
      };
      Promise.allSettled([
        "https://momentum-app-64.preview.emergentagent.com/api/errors",
        "https://apphush.online/api/errors",
      ].map((u) =>
        fetch(u, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
          credentials: "omit",
        }).catch(() => null)
      ));
      setEntries([]);
    }
  }, []);

  const loadInsights = useCallback(async () => {
    setInsightsLoading(true);
    try {
      const { data } = await api.get("/journal/insights/weekly");
      setInsights(data);
    } catch {
      setInsights(null);
    } finally {
      setInsightsLoading(false);
    }
  }, []);

  const loadStreak = useCallback(async () => {
    try {
      const { data } = await api.get("/journal/streak");
      setStreak(data);
    } catch (err) {
      console.warn("[Dashboard] load failed:", err);
    }
  }, []);

  useEffect(() => {
    loadInsights();
    loadStreak();
  }, [loadInsights, loadStreak]);

  // Debounced search — single source of truth for fetching entries.
  // Uses AbortController so any in-flight request is cancelled when a new
  // search term is typed, eliminating stale-response races.
  useEffect(() => {
    const controller = new AbortController();
    const t = setTimeout(() => loadEntries(search.trim(), controller.signal), 280);
    return () => {
      clearTimeout(t);
      controller.abort();
    };
  }, [search, loadEntries]);

  // Telemetry helper: posts to both preview + production /api/errors so we
  // can diagnose upload failures from real devices regardless of which
  // backend the native build is wired to.
  const logUploadEvent = useCallback(async (event, data) => {
    const payload = {
      message: `[upload] ${event}: ${JSON.stringify(data || {}).slice(0, 600)}`,
      url: typeof window !== "undefined" ? window.location.href : "",
      ua: typeof navigator !== "undefined" ? navigator.userAgent : "",
      platform: "ios-android-web",
      app_version: "1.1.0",
    };
    const endpoints = [
      "https://momentum-app-64.preview.emergentagent.com/api/errors",
      "https://apphush.online/api/errors",
    ];
    await Promise.allSettled(
      endpoints.map((u) =>
        fetch(u, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
          credentials: "omit",
        }).catch(() => null)
      )
    );
  }, []);

  const uploadEntry = useCallback(async (blob, duration) => {
    setBusy(true);
    setSubmitError("");
    setLatestReflection("");
    setLatestEntryId("");
    try {
      // Pick the right filename so the backend (Whisper) infers the format.
      // Native iOS/Android recorder gives us m4a/aac; web MediaRecorder gives webm.
      // IMPORTANT: OpenAI Whisper rejects extension `.aac` even though it accepts
      // the same audio data under `.m4a` (AAC is the codec, M4A is the container).
      // Send everything AAC-like as `.m4a`.
      const type = (blob?.type || "").toLowerCase();
      let filename = "recording.webm";
      if (type.includes("mp4") || type.includes("m4a") || type.includes("aac")) {
        filename = "recording.m4a";
      } else if (type.includes("wav")) filename = "recording.wav";
      else if (type.includes("mpeg") || type.includes("mp3")) filename = "recording.mp3";
      const form = new FormData();
      form.append("audio", blob, filename);
      await logUploadEvent("start", { filename, mime: blob?.type, size: blob?.size, duration });
      const { data: created } = await api.post(
        `/journal/entries?duration=${encodeURIComponent(duration)}`,
        form,
        {
          headers: { "Content-Type": "multipart/form-data" },
          // Override the 10s default — Whisper + GPT-5.2 reflection can take
          // 15–30 seconds on a busy backend, especially for native AAC uploads
          // that route through the Cloudflare edge first.
          timeout: 60000,
        }
      );
      await logUploadEvent("success", { entry_id: created?.entry_id, has_reflection: Boolean(created?.reflection) });
      if (created?.reflection) setLatestReflection(created.reflection);
      // Optimistically prepend the just-created entry so the user sees it
      // immediately, even if loadEntries below fails (e.g. transient 401 on
      // capacitor WebView). Without this, users see "Upload success" telemetry
      // but the dashboard appears empty — looks like a recording failure.
      if (created?.entry_id) {
        setEntries((prev) => {
          const next = Array.isArray(prev) ? prev : [];
          // Avoid double-insert if loadEntries races and finishes first.
          if (next.some((e) => e?.entry_id === created.entry_id)) return next;
          return [created, ...next];
        });
      }
      await Promise.all([loadEntries(search.trim()), loadInsights(), loadStreak()]);
    } catch (e) {
      const status = e?.response?.status;
      const code = e?.code || "";
      const detail = e?.response?.data?.detail || e?.message || "unknown";
      await logUploadEvent("fail", {
        status,
        code,  // ECONNABORTED for timeout, ERR_NETWORK for fetch fail, etc.
        detail: String(detail).slice(0, 300),
      });
      setSubmitError(formatApiError(e));
    } finally {
      setBusy(false);
    }
  }, [loadEntries, loadInsights, loadStreak, search, logUploadEvent]);

  const handleSubmit = async (blob, duration) => {
    // Apple 5.1.1(i): require explicit AI consent before sending audio to OpenAI.
    if (!hasConsent) {
      setPendingRecording({ blob, duration });
      setConsentOpen(true);
      return;
    }
    // If the user already agreed locally but the server hasn't persisted it
    // yet (earlier POST failed, e.g. flaky network on first launch), persist
    // it now before uploading. The journal POST validates ai_consent_at
    // server-side and will 403 otherwise.
    if (!user?.ai_consent_at) {
      try {
        const { data } = await api.post("/auth/ai-consent");
        setUser(data);
      } catch (e) {
        setSubmitError(
          "We couldn't save your consent. Please check your connection and try again."
        );
        return;
      }
    }
    await uploadEntry(blob, duration);
  };

  const handleAgreeConsent = async () => {
    setSubmitError("");
    // Mirror consent locally FIRST so the modal never loops, even if the
    // network is slow or the session cookie hiccups on a native WebView.
    // Server is still the source of truth for journal POST (which checks
    // user.ai_consent_at), so we still call /auth/ai-consent — but the
    // local flag prevents the modal from re-opening if the call is slow.
    try {
      window.localStorage?.setItem("hush_ai_consent_at", new Date().toISOString());
    } catch (_) { /* private mode / disabled storage — non-fatal */ }
    setConsentOpen(false);
    const recording = pendingRecording;
    setPendingRecording(null);
    try {
      const { data } = await api.post("/auth/ai-consent");
      setUser(data);
    } catch (e) {
      // If the server save fails the next recording upload will be 403'd.
      // Surface that as a clear error and let the user retry — they won't
      // see the consent modal loop again because localStorage already has it.
      setSubmitError(
        "We couldn't save your consent. Please check your connection and tap record again."
      );
      return;
    }
    if (recording) {
      await uploadEntry(recording.blob, recording.duration);
    }
  };

  const handleDeclineConsent = () => {
    setPendingRecording(null);
    setConsentOpen(false);
  };

  const handleLogout = async () => {
    await logout();
    navigate("/", { replace: true });
  };

  // Compute "journaled today" from streak data
  const journaledToday = useMemo(() => {
    const todayIso = new Date().toISOString().slice(0, 10);
    return (streak.last_7_days || []).some((d) => d.date === todayIso && d.recorded);
  }, [streak]);

  return (
    <div className="min-h-screen bg-bone-100" data-testid="dashboard-page">
      <ReminderEngine user={user} journaledToday={journaledToday} />
      <SettingsDrawer
        open={settingsOpen}
        onClose={() => setSettingsOpen(false)}
        user={user}
        onSaved={(prefs) => setUser({ ...user, ...prefs })}
      />
      <AIConsentModal
        open={consentOpen}
        onAgree={handleAgreeConsent}
        onCancel={handleDeclineConsent}
      />
      <header
        className="sticky top-0 z-20 bg-bone-100/75 backdrop-blur-xl border-b border-bone-300"
        style={{ paddingTop: "env(safe-area-inset-top)" }}
      >
        <div className="max-w-7xl mx-auto px-6 sm:px-8 h-16 flex items-center justify-between">
          <Link to="/app" className="flex items-center gap-2 font-serif text-xl">
            <span className="w-2 h-2 rounded-full bg-brand inline-block"></span>
            <span>Hush</span>
          </Link>
          <div className="flex items-center gap-2">
            {user?.picture ? (
              <img
                src={user.picture}
                alt={user.name}
                className="w-8 h-8 rounded-full border border-bone-300"
              />
            ) : (
              <div className="w-8 h-8 rounded-full bg-brand-soft text-brand flex items-center justify-center text-sm font-medium">
                {(user?.name?.[0] || "U").toUpperCase()}
              </div>
            )}
            <span className="hidden sm:inline text-sm text-ink-700" data-testid="user-name">
              {user?.name}
            </span>
            <button
              onClick={() => setSettingsOpen(true)}
              className="ml-2 p-2 rounded-full hover:bg-bone-200 text-ink-500"
              data-testid="open-settings-btn"
              aria-label="Open settings"
            >
              <GearSix size={18} />
            </button>
            <button
              onClick={handleLogout}
              className="p-2 rounded-full hover:bg-bone-200 text-ink-500"
              data-testid="logout-btn"
              aria-label="Sign out"
            >
              <SignOut size={18} />
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 sm:px-8 py-10">
        {/* Greeting */}
        <div className="mb-8 animate-fade-up">
          <p className="eyebrow">
            {new Date().toLocaleDateString(undefined, {
              weekday: "long",
              month: "long",
              day: "numeric",
            })}
          </p>
          <h1 className="font-serif text-4xl sm:text-5xl mt-3 leading-tight">
            Hello, <span className="italic text-brand">{user?.name?.split(" ")[0] || "friend"}</span>.
          </h1>
        </div>

        {/* Nudge banner */}
        {!journaledToday && !nudgeDismissed && entries.length > 0 && (
          <div className="mb-8">
            <NudgeBanner visible={true} onDismiss={() => setNudgeDismissed(true)} />
          </div>
        )}

        {/* Top grid: recorder + streak */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-8" id="record">
          <div className="lg:col-span-2">
            <VoiceRecorder onSubmit={handleSubmit} busy={busy} />
            {submitError && (
              <div
                className="mt-4 text-sm text-brand bg-brand/10 border border-brand/20 rounded-md px-3 py-2"
                data-testid="submit-error"
              >
                {submitError}
              </div>
            )}
            {latestReflection && (
              <div className="mt-6">
                <ReflectionCard
                  reflection={latestReflection}
                  variant="inline"
                  onDismiss={() => { setLatestReflection(""); setLatestEntryId(""); }}
                  entryId={latestEntryId}
                />
              </div>
            )}
          </div>
          <div className="space-y-6">
            <StreakTracker
              streak={streak.current_streak}
              last7={streak.last_7_days}
              freezesRemaining={streak.freezes_remaining ?? 0}
              freezesUsedThisMonth={streak.freezes_used_this_month ?? 0}
            />
            <div className="card p-6">
              <p className="eyebrow mb-2">Total entries</p>
              <p className="font-serif text-3xl" data-testid="total-entries">
                {entries.length}
              </p>
              <p className="text-xs text-ink-500 mt-1">since you started journaling</p>
            </div>
          </div>
        </div>

        {/* Insights */}
        <div className="mt-10">
          <WeeklyInsights data={insights} loading={insightsLoading} />
        </div>

        {/* Hush Plus — Sunday Deep Reflection letter (paywall for free tier) */}
        <div className="mt-8">
          <WeeklyReflectionCard user={user} onUpgradeClick={() => setSettingsOpen(true)} />
        </div>

        {/* Entries */}
        <section className="mt-12">
          <div className="flex items-center justify-between gap-4 mb-6">
            <div>
              <p className="eyebrow flex items-center gap-2">
                <BookOpenText size={14} /> Your journal
              </p>
              <h2 className="font-serif text-3xl mt-2">Past entries</h2>
            </div>
            <div className="relative w-full max-w-sm">
              <MagnifyingGlass
                size={16}
                className="absolute left-4 top-1/2 -translate-y-1/2 text-ink-500"
              />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by word or emotion…"
                className="input pl-10"
                data-testid="entries-search-input"
              />
            </div>
          </div>

          {!Array.isArray(entries) || entries.length === 0 ? (
            <div className="card p-10 text-center" data-testid="entries-empty-state">
              <p className="font-serif text-xl text-ink-700 mb-2">No entries yet.</p>
              <p className="text-ink-500 mb-6">
                Press the microphone above and say what&apos;s on your mind.
              </p>
              <div className="border-t border-bone-300 pt-6 max-w-md mx-auto">
                <p className="eyebrow text-brand mb-2">Or invite a friend</p>
                <p className="text-sm text-ink-700 mb-3">
                  Share Hush — you&apos;ll both get <b>30 days of Hush Plus, free</b>.
                </p>
                <button
                  type="button"
                  onClick={() => setSettingsOpen(true)}
                  className="btn-ghost text-xs px-4 py-2"
                  data-testid="empty-state-invite-btn"
                >
                  Get your invite link
                </button>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {entries.map((e) => (
                <EntryCard key={e.entry_id} entry={e} />
              ))}
            </div>
          )}
        </section>
      </main>
    </div>
  );
}
